import { useState, useMemo } from 'react';
import { Layers, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ListingLoadingLabel } from '../../common/components/ListingLoadingLabel';
import type { SchemaFilter } from '../../core/types/workspaceTypes';
import { SchemaPickerDialog } from './SchemaPickerDialog';

type SchemaFilterSectionProps = {
  /** Schemas available on the source database (or undefined while loading). */
  sourceSchemas: string[] | undefined;
  /** Schemas available on the target database (or undefined while loading). */
  targetSchemas: string[] | undefined;
  /** True when either side's schemas are still loading. */
  isLoading?: boolean;
  /** True when both source and target are selected. */
  hasBothEndpoints: boolean;
  /** Current filter (applies to both source and target). */
  filter?: SchemaFilter;
  onFilterChange: (next: SchemaFilter | undefined) => void;
  /** Whether system schemas (sys, INFORMATION_SCHEMA, …) are included. */
  includeSystemSchemas: boolean;
  onIncludeSystemSchemasChange: (next: boolean) => void;
};

export function SchemaFilterSection({
  sourceSchemas,
  targetSchemas,
  isLoading,
  hasBothEndpoints,
  filter,
  onFilterChange,
  includeSystemSchemas,
  onIncludeSystemSchemasChange,
}: SchemaFilterSectionProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const availableSchemas = useMemo(() => {
    const set = new Set<string>();
    for (const s of sourceSchemas ?? []) set.add(s);
    for (const s of targetSchemas ?? []) set.add(s);
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [sourceSchemas, targetSchemas]);

  const hasFilter = !!filter && filter.schemas.length > 0;

  const summary = useMemo(() => {
    if (isLoading && !hasBothEndpoints) return '';
    if (!hasFilter) {
      return includeSystemSchemas
        ? 'All schemas (system schemas included)'
        : 'All schemas (system schemas excluded)';
    }
    const verb = filter!.mode === 'include' ? 'Including' : 'Excluding';
    const count = `${filter!.schemas.length} ${
      filter!.schemas.length === 1 ? 'schema' : 'schemas'
    }`;
    const sysSuffix = includeSystemSchemas ? ', system schemas included' : '';
    return `${verb} ${count}${sysSuffix}`;
  }, [hasFilter, filter, includeSystemSchemas, isLoading, hasBothEndpoints]);

  const removeSchema = (schema: string) => {
    if (!filter) return;
    const next = filter.schemas.filter((s) => s !== schema);
    onFilterChange(next.length === 0 ? undefined : { mode: filter.mode, schemas: next });
  };

  const handleClear = () => onFilterChange(undefined);

  return (
    <div
      className="rounded-lg border px-3 py-2 space-y-2"
      data-testid="schema-filter-section"
    >
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span
          className="text-sm flex-1 min-w-0 truncate"
          data-testid="schema-filter-summary"
          title={summary}
        >
          {summary}
        </span>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={handleClear}
            data-testid="schema-filter-clear"
          >
            Reset
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 flex-shrink-0"
          disabled={!hasBothEndpoints || isLoading}
          onClick={() => setPickerOpen(true)}
          data-testid="schema-filter-edit"
        >
          {isLoading ? (
            /* Soporte C3 — name what is loading. `getTables` (which derives
               these schemas) has no separable connect step, so there is no
               phase to report here — just an honest static label. */
            <ListingLoadingLabel target="schemas" size={10} />
          ) : (
            <>
              <Pencil className="h-3 w-3" />
              Edit…
            </>
          )}
        </Button>
      </div>

      {/* Active-filter chips — only render when a filter is set */}
      {hasFilter && (
        <div
          className="flex flex-wrap gap-1.5 pl-6"
          data-testid="schema-filter-chips"
        >
          {filter!.schemas.map((schema) => (
            <Badge
              key={schema}
              variant="secondary"
              className="gap-1"
              data-testid={`schema-filter-chip-${schema}`}
            >
              <span className="font-mono text-xs">{schema || '(default)'}</span>
              <button
                type="button"
                aria-label={`Remove ${schema}`}
                onClick={() => removeSchema(schema)}
                className="hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <SchemaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        availableSchemas={availableSchemas}
        value={filter}
        onApply={onFilterChange}
        includeSystemSchemas={includeSystemSchemas}
        onIncludeSystemSchemasChange={onIncludeSystemSchemasChange}
      />
    </div>
  );
}
